import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  TablePagination,
  Button,
  Paper,
  ButtonBase,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Remove,
  Edit,
  Visibility,
  Add,
  ContentCopy,
  Mail,
  Block
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import db from '../../services/db';
import { format } from 'date-fns';

function PedidosList() {
  const [pedidos, setPedidos] = useState([]);
  const [filters, setFilters] = useState({
    dataRef: '',
    codigo: '',
    cliente: '',
    statusNota: 'Todos',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Busca os pedidos do banco de dados, ordenando os não sincronizados para o início
  const fetchPedidos = async () => {
    try {
      const allPedidos = await db.pedidos.toArray();

      // Ordena: pedidos com sincronismo=false aparecem primeiro
      const sortedPedidos = allPedidos
        .filter((pedido) => pedido.status !== 'D' || pedido.statusSefaz === 'Cancelada')
        .sort((a, b) => {
         
         
          return new Date(b.dataPedido) - new Date(a.dataPedido); // Pedidos mais recentes primeiro
        });


      setPedidos(sortedPedidos);
      console.log(sortedPedidos);
    } catch (error) {
      console.error('Erro ao buscar pedidos:', error);
    }
  };

  useEffect(() => {
    fetchPedidos();
  }, []);
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchPedidos();
    }, 10000); // 10000 milissegundos = 10 segundos
  
    return () => clearInterval(intervalId); // Limpa o interval ao desmontar o componente
  }, []);


  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy HH:mm');
    } catch {
      return '-';
    }
  };

  // Renderiza o status do pedido com ícones e textos
  const renderStatus = (status) => {
    switch (status) {
      case 'A':
        return (
          <Box sx={{ textAlign: 'center' }}>
            🟡 <br />
            <strong>Analise Concluida</strong>
          </Box>
        );
      case 'C':
        return (
          <Box sx={{ textAlign: 'center' }}>
            🟢 <br />
            <strong>Concluído</strong>
          </Box>
        );
      case 'P':
        return (
          <Box sx={{ textAlign: 'center' }}>
            🟠 <br />
            <strong>Pendente</strong>
          </Box>
        );
      case 'N':
        return (
          <Box sx={{ textAlign: 'center' }}>
            🔴 <br />
            <strong>Cancelado</strong>
          </Box>
        );
      case 'I':
        return (
          <Box sx={{ textAlign: 'center' }}>
            🟠 <br />
            <strong>Em analise</strong>
          </Box>
        );
      default:
        return '-';
    }
  };

  // Função para duplicar o pedido
  const handleDuplicate = async (pedidoId) => {
    try {
      navigate(`/pedidos/duplicar/${pedidoId}`);
    } catch (error) {
      console.error('Erro ao duplicar o pedido:', error);
    }
  };

  // Filtra os pedidos com base nos filtros informados
  const filteredPedidos = pedidos.filter((pedido) => {
    return (
      (!filters.codigo || pedido.numeroPedido.includes(filters.codigo)) &&
      (!filters.cliente ||
        pedido.nomeCliente?.toLowerCase().includes(filters.cliente.toLowerCase())) &&
      (!filters.statusNota ||
        filters.statusNota === 'Todos' ||
        pedido.statusNota === filters.statusNota)
    );
  });

  return (
    <Box sx={{ padding: { xs: 1, sm: 2 } }}>
      <Typography variant="h4" align="center" sx={{ marginBottom: 2 }}>
        Pedido de Venda
      </Typography>

      {/* Filtros */}
      <Grid container spacing={2} sx={{ marginBottom: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Numero Pedido"
            variant="outlined"
            size="small"
            value={filters.codigo}
            onChange={(e) => handleFilterChange('codigo', e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Cliente"
            variant="outlined"
            size="small"
            value={filters.cliente}
            onChange={(e) => handleFilterChange('cliente', e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl size="small" fullWidth>
            <Select
              value={filters.statusNota}
              onChange={(e) => handleFilterChange('statusNota', e.target.value)}
              displayEmpty
            >
              <MenuItem value="A">Aberto</MenuItem>
              <MenuItem value="D">Excluído</MenuItem>
              <MenuItem value="P">Pendente</MenuItem>
              <MenuItem value="C">Concluído</MenuItem>
              <MenuItem value="D">Cancelado</MenuItem>
              <MenuItem value="Todos">Todos</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Botão Novo Centralizado */}
      <Box sx={{ marginTop: 3, textAlign: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => navigate('/pedidos/edit')}
          sx={{ maxWidth: 200 }}
        >
          Novo Pedido
        </Button>
      </Box>

      {/* Tabela de Pedidos */}
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Dt. Pedido</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>UUID</TableCell>
              {!isSmallScreen && <TableCell>Sincronismo</TableCell>}
              {!isSmallScreen && <TableCell>NFE</TableCell>}
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPedidos
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((pedido, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ py: 0.5 }}>{(pedido.numeroPedido == undefined? 'OFFLINE':pedido.numeroPedido)}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{pedido.nomeCliente || '-'}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{formatDate(pedido.dataPedido)}</TableCell>
                  
                    <TableCell sx={{ py: 0.5 }}>{pedido.valorTotal}</TableCell>
                  
                  <TableCell sx={{ py: 0.5 }}>{renderStatus(pedido.status)}</TableCell>
                     <TableCell sx={{ py: 0.5 }}>{pedido.uuidMobile}</TableCell>
                  {!isSmallScreen && (
                    <TableCell sx={{ py: 0.5 }}>
                      {pedido.sincronismo ? (
                        <Box sx={{ textAlign: 'center' }}>

                          <CheckCircle color="success" fontSize="small" />
                          <Typography variant="caption" sx={{ mt: 0.5, color: 'green' }}>
                            SINCRONIZADO
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: 'center' }}>
                          <Remove sx={{ color: 'gray', fontSize: 'small' }} />


                          <Typography variant="caption" sx={{ mt: 0.5, color: 'green' }}>
                            OFFLINE
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  )}
                  {!isSmallScreen && (
                    <TableCell sx={{ py: 0.5 }}>
                      {pedido.status === 'D' && pedido.statusSefaz === 'Cancelada' ? (
                        <Box sx={{ textAlign: 'center' }}>
                          <Cancel color="error" fontSize="small" />
                          <br />

                          <Typography variant="caption" sx={{ mt: 0.5, color: 'red' }}>
                            CANCELADA
                          </Typography>
                        </Box>



                      ) : pedido.statusNota === '-' || pedido.statusSefaz == null ? (
                        <Box sx={{ textAlign: 'center' }}>
                          <Remove color="disabled" fontSize="small" />
                          <br />

                          <Typography variant="caption" sx={{ mt: 0.5 }}>
                            NÃO GERADA
                          </Typography>
                        </Box>

                      ) : (
                        <Box sx={{ textAlign: 'center' }}>
                          <CheckCircle color="success" fontSize="small" />

                          <br />
                          <Typography variant="caption" sx={{ mt: 0.5 }}>

                            {pedido.statusSefaz}
                          </Typography>
                        </Box>

                      )}
                    </TableCell>
                  )}
                  <TableCell sx={{ py: 0.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row', // força que seja sempre linha
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      {/* Ação Editar */}
                      <ButtonBase
                        onClick={() => navigate(`/pedidos/edit/${pedido.idLocal}`)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 1,
                        }}
                        // Desabilita se a série não for 'MB' OU se o status não for 'I'
                        disabled={pedido.descricaoSerie !== 'PDM' || pedido.status !== 'I'}
                      >
                        {/* Se for MB e status I, mostra o ícone de edição; caso contrário, "bloqueado" */}
                        {(pedido.descricaoSerie === 'PDM' && pedido.status === 'I') ? (
                          <Edit fontSize="small" sx={{ color: 'orange' }} />
                        ) : (
                          <Block fontSize="small" sx={{ color: 'grey' }} />
                        )}

                        <Typography
                          variant="caption"
                          sx={{
                            mt: 0.5,
                            // Texto alaranjado se habilitado, cinza se desabilitado
                            color: (pedido.descricaoSerie === 'PDM' && pedido.status === 'I')
                              ? 'orange'
                              : 'grey',
                          }}
                        >
                          EDITAR
                        </Typography>
                      </ButtonBase>

                      {/* Ação Excluir */}
                      <ButtonBase
                        onClick={() => console.log(`Excluindo pedido ${pedido.idLocal}`)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 1,
                        }}
                        disabled={!(pedido.descricaoSerie === 'MB' && pedido.status != 'I')}
                      >

                        {!(pedido.descricaoSerie === 'MB' && !pedido.sincronismo) ? (
                          <Block fontSize="small" sx={{ color: 'grey' }} />
                        ) : (
                          <Cancel fontSize="small" sx={{ color: 'red' }} />
                        )}



                        <Typography variant="caption" sx={{ mt: 0.5, color: 'red' }}>
                          EXCLUIR
                        </Typography>
                      </ButtonBase>

                      {/* Ação Consultar */}
                      <ButtonBase
                        onClick={() => navigate(`/pedidos/view/${pedido.idLocal}`)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 1,
                        }}
                      >
                        <Visibility fontSize="small" sx={{ color: 'blue' }} />
                        <Typography variant="caption" sx={{ mt: 0.5, color: 'blue' }}>
                          CONSULTAR
                        </Typography>
                      </ButtonBase>

                      {/* Ação Duplicar */}
                      <ButtonBase
                        onClick={() => navigate(`/pedidos/duplicar/${pedido.idLocal}`) }
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 1,
                        }}
                      >
                        <ContentCopy fontSize="small" sx={{ color: theme.palette.secondary.main }} />
                        <Typography
                          variant="caption"
                          sx={{ mt: 0.5, color: theme.palette.secondary.main }}
                        >
                          DUPLICAR
                        </Typography>
                      </ButtonBase>

                      {/* Ação Enviar E-mail */}
                      {/* Ação Enviar E-mail / Imprimir */}
                      <ButtonBase
                        onClick={() => navigate(`/pedidos/imprimir/${pedido.idLocal}`)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 1,
                        }}
                      >
                        <Mail fontSize="small" />
                        <Typography variant="caption" sx={{ mt: 0.5 }}>
                          ENVIAR E-MAIL
                        </Typography>
                      </ButtonBase>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredPedidos.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Legenda */}
      <Box sx={{ marginTop: 3 }}>
        <Typography variant="subtitle1">
          <strong>Legenda:</strong>
        </Typography>
        <Typography> 🟡 Analise Concluida | 🟢 Concluído | 🟠 Em analise | 🔴 Cancelado</Typography>
      </Box>
    </Box>
  );
}

 



export default PedidosList;
